import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { credentialingService } from '../../../services/credentialingService';

const CredentialsList = ({ refreshTrigger }) => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('expirationDate');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    loadCredentials();
  }, [refreshTrigger]);

  const loadCredentials = async () => {
    try {
      setLoading(true);
      const data = await credentialingService?.getProviderCredentials();
      setCredentials(data);
    } catch (err) {
      setError(err?.message || 'Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success';
      case 'expiring_soon':
        return 'bg-warning/10 text-warning border-warning';
      case 'expired':
        return 'bg-destructive/10 text-destructive border-destructive';
      case 'pending_renewal':
        return 'bg-primary/10 text-primary border-primary';
      case 'suspended':
        return 'bg-accent/10 text-accent border-accent';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatCredentialType = (type) => {
    return type?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getDaysUntilExpiration = (expirationDate) => {
    if (!expirationDate) return null;
    const today = new Date();
    const expDate = new Date(expirationDate);
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredCredentials = credentials?.filter(cred =>
    cred?.credentialType?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    cred?.credentialNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    cred?.issuingAuthority?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const sortedCredentials = [...filteredCredentials]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];
    
    if (sortField === 'expirationDate' || sortField === 'issueDate') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="relative w-full sm:w-64">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search credentials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 h-10 bg-muted border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <p className="text-sm text-muted-foreground">
          {sortedCredentials?.length} credential{sortedCredentials?.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('credentialType')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Type
                  <Icon name={sortField === 'credentialType' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left hidden md:table-cell">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Number</span>
              </th>
              <th className="px-4 py-3 text-left hidden lg:table-cell">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Issuing Authority</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('expirationDate')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Expiration
                  <Icon name={sortField === 'expirationDate' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</span>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedCredentials?.map((credential) => {
              const daysUntilExpiration = getDaysUntilExpiration(credential?.expirationDate);
              return (
                <tr key={credential?.id} className="hover:bg-muted/30 transition-smooth">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                        <Icon name="Award" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{formatCredentialType(credential?.credentialType)}</p>
                        {credential?.specialty && (
                          <p className="text-xs text-muted-foreground">{credential?.specialty}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 hidden md:table-cell">
                    <p className="text-sm text-foreground font-mono">{credential?.credentialNumber}</p>
                    {credential?.state && (
                      <p className="text-xs text-muted-foreground">{credential?.state}</p>
                    )}
                  </td>
                  <td className="px-4 py-4 hidden lg:table-cell">
                    <p className="text-sm text-foreground">{credential?.issuingAuthority}</p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-foreground">{formatDate(credential?.expirationDate)}</p>
                    {daysUntilExpiration !== null && (
                      <p className={`text-xs ${
                        daysUntilExpiration < 0 ? 'text-destructive' :
                        daysUntilExpiration <= 30 ? 'text-warning': 'text-muted-foreground'
                      }`}>
                        {daysUntilExpiration < 0 ? `Expired ${Math.abs(daysUntilExpiration)} days ago` :
                         daysUntilExpiration === 0 ? 'Expires today' :
                         `${daysUntilExpiration} days remaining`}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(credential?.status)}`}>
                      {credential?.status?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" iconName="Eye" />
                      <Button variant="ghost" size="sm" iconName="Edit" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedCredentials?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
            <Icon name="Award" size={32} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-base font-medium text-foreground mb-2">No Credentials Found</p>
          <p className="text-sm text-muted-foreground">Add your first credential to get started</p>
        </div>
      )}
    </div>
  );
};

export default CredentialsList;